/**
 * What changed between two approved revisions, at a glance.
 *
 * Four disclosures over one accordion, with the programme impact always open
 * below them. Each one carries its own count in its subtitle, so a reader
 * decides what to open before opening anything.
 *
 * @module components/portfolio/comparison/ComparisonOverview
 */

import React from "react";

import { AssessmentProfile } from "@/components/portfolio/comparison/AssessmentProfile";
import type { ComparisonArea } from "@/components/portfolio/comparison/comparison-areas";
import type { ModuleDifference } from "@/components/portfolio/comparison/diff-types";
import { ModuleInventory } from "@/components/portfolio/comparison/ModuleInventory";
import { ProgrammeImpact } from "@/components/portfolio/comparison/ProgrammeImpact";
import { ProgrammeOutcomeInventory } from "@/components/portfolio/comparison/ProgrammeOutcomeInventory";
import { Accordion, AccordionItem, Button, Heading, Icon, MutedText } from "@/components/ui";
import type { CurriculumRevision, ProgrammeIdentityFieldChange } from "@/server/types";
import type { AssessmentTypeSlice } from "@/utils/assessments";

export interface ComparisonAreaCount {
  id: ComparisonArea;
  label: string;
  count: number;
}

export interface ComparisonOverviewProps {
  /** The areas carrying at least one change, each with its own count. */
  areas: readonly ComparisonAreaCount[];
  totalChanges: number;
  /** Opens the change register filtered to one area. */
  onOpenArea: (area: ComparisonArea) => void;
  /** Absent until both revisions have been chosen. */
  fromRevision?: CurriculumRevision;
  toRevision?: CurriculumRevision;
  moduleDifferences: readonly ModuleDifference[];
  changedModuleCount: number;
  outcomeChangeCount: number;
  earlierAssessmentProfile: readonly AssessmentTypeSlice[];
  laterAssessmentProfile: readonly AssessmentTypeSlice[];
  identityChanges: readonly ProgrammeIdentityFieldChange[];
  affectedOutputs: readonly string[];
  /** Changes when the reader asks to be shown the impact panel. */
  impactFocus?: number;
}

/**
 * @example
 * ```tsx
 * <ComparisonOverview areas={areas} totalChanges={changes.length} onOpenArea={openRegister} … />
 * ```
 */
export function ComparisonOverview({
  areas,
  totalChanges,
  onOpenArea,
  fromRevision = undefined,
  toRevision = undefined,
  moduleDifferences,
  changedModuleCount,
  outcomeChangeCount,
  earlierAssessmentProfile,
  laterAssessmentProfile,
  identityChanges,
  affectedOutputs,
  impactFocus = 0,
}: ComparisonOverviewProps): React.ReactElement {
  return (
    <section aria-labelledby="comparison-overview-heading">
      <Heading level={2} id="comparison-overview-heading" className="sr-only">
        Comparison overview
      </Heading>
      <div className="space-y-4">
        <Accordion
          id="comparison-overview"
          defaultExpandedKeys={["comparison-overview-changes"]}
          className="space-y-4"
        >
          <AccordionItem
            eventKey="comparison-overview-changes"
            headingLevel={3}
            title="What changed"
            subtitle={`${totalChanges} academic and programme change${totalChanges === 1 ? "" : "s"} across ${areas.length} area${areas.length === 1 ? "" : "s"}`}
            data-testid="comparison-overview-changes"
          >
            {/* The register beside this is gated the same way: a re-approval
                that only bumps a timestamp leaves no area, and an instruction
                over an empty row asks for a control the screen does not
                ship. */}
            <MutedText>
              {areas.length === 0
                ? "These revisions contain the same programme content"
                : "Select an area to open its entries in the change register."}
            </MutedText>
            <div className="mt-3 flex flex-wrap gap-2">
              {areas.map((area) => (
                <Button
                  key={area.id}
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    onOpenArea(area.id);
                  }}
                >
                  {/* The whole name in one node: an `aria-label` overrides
                      the content and drops the count, which is the basis for
                      choosing an area, and a spoken word in a node of its own
                      runs into the visible text beside it. */}
                  <span aria-hidden>
                    {area.count} {area.label}
                  </span>
                  <span className="sr-only">{`${area.count} ${area.label} change${area.count === 1 ? "" : "s"}`}</span>
                  <Icon name="arrow-right" aria-hidden />
                </Button>
              ))}
            </div>
          </AccordionItem>
          {fromRevision && toRevision ? (
            <>
              <AccordionItem
                eventKey="comparison-overview-outcomes"
                headingLevel={3}
                title="Programme learning outcomes"
                subtitle={`${fromRevision.data.plos?.length ?? 0} earlier, ${toRevision.data.plos?.length ?? 0} later · ${outcomeChangeCount} change${outcomeChangeCount === 1 ? "" : "s"}`}
                data-testid="comparison-overview-outcomes"
              >
                <MutedText className="mb-4">
                  Compare outcomes carried forward, amended, added, or removed.
                </MutedText>
                <div className="grid gap-6 lg:grid-cols-2">
                  <ProgrammeOutcomeInventory
                    label={fromRevision.institutionalLabel}
                    programme={fromRevision.data}
                    otherProgramme={toRevision.data}
                    side="before"
                  />
                  <ProgrammeOutcomeInventory
                    label={toRevision.institutionalLabel}
                    programme={toRevision.data}
                    otherProgramme={fromRevision.data}
                    side="after"
                  />
                </div>
              </AccordionItem>

              <AccordionItem
                eventKey="comparison-overview-modules"
                headingLevel={3}
                title="Module inventory"
                subtitle={`${fromRevision.data.modules?.length ?? 0} earlier, ${toRevision.data.modules?.length ?? 0} later · ${changedModuleCount} changed`}
                data-testid="comparison-overview-modules"
              >
                <MutedText className="mb-4">
                  Compare the modules present in each approved version. Titles are shown first, with
                  codes alongside.
                </MutedText>
                <div className="grid gap-6 lg:grid-cols-2">
                  <ModuleInventory
                    label={fromRevision.institutionalLabel}
                    programme={fromRevision.data}
                    differences={moduleDifferences}
                    side="before"
                  />
                  <ModuleInventory
                    label={toRevision.institutionalLabel}
                    programme={toRevision.data}
                    differences={moduleDifferences}
                    side="after"
                  />
                </div>
              </AccordionItem>

              <AccordionItem
                eventKey="comparison-overview-assessments"
                headingLevel={3}
                title="Assessment profile"
                subtitle={`${earlierAssessmentProfile.length} earlier assessment type${earlierAssessmentProfile.length === 1 ? "" : "s"}, ${laterAssessmentProfile.length} later assessment type${laterAssessmentProfile.length === 1 ? "" : "s"}`}
                data-testid="comparison-overview-assessments"
              >
                <MutedText className="mb-4">
                  Shows each assessment tool&apos;s share of programme assessment, weighted by
                  module credits.
                </MutedText>
                <div className="grid gap-6 lg:grid-cols-2">
                  <AssessmentProfile
                    label={fromRevision.institutionalLabel}
                    slices={earlierAssessmentProfile}
                  />
                  <AssessmentProfile
                    label={toRevision.institutionalLabel}
                    slices={laterAssessmentProfile}
                  />
                </div>
              </AccordionItem>
            </>
          ) : null}
        </Accordion>

        <ProgrammeImpact
          headingLevel={3}
          identityChanges={identityChanges}
          affectedOutputs={affectedOutputs}
          focusToken={impactFocus}
        />
      </div>
    </section>
  );
}
